// packages/shared/src/vuln-classes.ts
//
// KAN-237 P6 — CISO-facing vulnerability-class taxonomy for the Analytics
// Compare "vuln-class" dimension. Analogue of SECTOR_BUCKETS (sectors.ts):
// a small, stable, CISO-legible set of classes, each mapping a list of exact
// CWE IDs. Raw `CWE-79` is NOT a picker category — it rolls up to a class.
//
// Mapping rule: exact `CWE-<n>` IDs only. CWE numbers are NOT hierarchical by
// digit prefix (CWE-78 / CWE-79 / CWE-787 are unrelated), so prefix matching is
// unsafe. Every CWE maps to exactly one class; an unrecognised CWE falls back
// to `other`. The `unclassified` bucket is a QUERY-TIME sentinel for incidents
// whose CVEs carry no CWE at all — it is NOT a CWE mapping.

export interface VulnClassBucket {
  /** URL-safe identifier, used in ?value= and the derived table's vuln_class. */
  value: string;
  /** Human-readable, CISO-facing label for the UI. */
  label: string;
  /** Short description of what the class covers. */
  description: string;
  /** Exact CWE IDs ("CWE-89") that map to this class. Empty for the catch-all
   *  `other` and the query-time `unclassified` sentinel. */
  cwes: string[];
}

/**
 * The vuln-class taxonomy (KAN-237 P6, ratified 2026-06-20). Order is the UI
 * listing order, NOT the tie-break priority (see {@link VULN_CLASS_PRIORITY}).
 */
export const VULN_CLASS_BUCKETS: VulnClassBucket[] = [
  {
    value: "web-application",
    label: "Web Application (XSS / CSRF / SSRF)",
    description:
      "Browser- and web-tier flaws: cross-site scripting, request forgery, server-side request forgery, open redirects.",
    cwes: ["CWE-79", "CWE-352", "CWE-918", "CWE-601"],
  },
  {
    value: "injection",
    label: "Injection",
    description:
      "Untrusted input interpreted as a command or query: SQL, OS command, LDAP, XPath, expression and code injection.",
    cwes: [
      "CWE-89",
      "CWE-77",
      "CWE-78",
      "CWE-90",
      "CWE-91",
      "CWE-94",
      "CWE-917",
      "CWE-74",
    ],
  },
  {
    value: "auth-session",
    label: "Authentication & Session",
    description:
      "Missing/improper authentication, authorization bypass, session fixation/expiry, and credential weaknesses.",
    cwes: [
      "CWE-287",
      "CWE-306",
      "CWE-862",
      "CWE-863",
      "CWE-384",
      "CWE-613",
      "CWE-798",
      "CWE-522",
    ],
  },
  {
    value: "access-control",
    label: "Access Control & Path",
    description:
      "Improper access control, insecure direct object references, path traversal, and incorrect permissions.",
    cwes: ["CWE-22", "CWE-284", "CWE-639", "CWE-732"],
  },
  {
    value: "crypto",
    label: "Cryptographic Weakness",
    description:
      "Weak/broken cryptography, missing or improper certificate validation, and cleartext exposure of sensitive data.",
    cwes: ["CWE-327", "CWE-326", "CWE-328", "CWE-295", "CWE-311", "CWE-321"],
  },
  {
    value: "memory-safety",
    label: "Memory Safety",
    description:
      "Out-of-bounds reads/writes, use-after-free, buffer overflows, NULL dereference, and integer overflow.",
    cwes: [
      "CWE-787",
      "CWE-125",
      "CWE-416",
      "CWE-119",
      "CWE-120",
      "CWE-476",
      "CWE-190",
    ],
  },
  {
    value: "deserialization",
    label: "Deserialization",
    description:
      "Unsafe deserialization of untrusted data and related object-injection flaws.",
    cwes: ["CWE-502", "CWE-913", "CWE-915"],
  },
  {
    value: "supply-chain",
    label: "Supply Chain",
    description:
      "Dependency, build-pipeline and CI/CD weaknesses, and embedded malicious code.",
    cwes: ["CWE-1357", "CWE-1395", "CWE-506"],
  },
  {
    value: "configuration",
    label: "Misconfiguration & Exposure",
    description:
      "Insecure default configuration and information exposure (including error-message and debug leakage).",
    cwes: ["CWE-16", "CWE-200", "CWE-209", "CWE-538"],
  },
  {
    value: "other",
    label: "Other (classified)",
    description:
      "A CWE is present but maps to none of the classes above.",
    cwes: [], // catch-all — assigned by classForCwe when no class matches
  },
  {
    value: "unclassified",
    label: "Unclassified (no CWE)",
    description:
      "No CWE enrichment is available: the incident's CVEs have no CWE mapping, or it has no CVE at all. Surfaced at query time, never dropped.",
    cwes: [], // query-time sentinel — NOT a CWE mapping
  },
];

/**
 * Tie-break priority for a CVE whose CWEs span more than one class (KAN-237 P6,
 * Captain-ratified fixed-priority order). When {@link dominantVulnClass} sees a
 * CVE mapping to multiple classes, the FIRST class in this list wins —
 * deterministic and explainable. `unclassified` is intentionally absent (it is
 * a query-time sentinel, never produced from a CWE set).
 */
export const VULN_CLASS_PRIORITY: string[] = [
  "memory-safety",
  "injection",
  "deserialization",
  "auth-session",
  "access-control",
  "crypto",
  "web-application",
  "supply-chain",
  "configuration",
  "other",
];

/** Slug for the query-time "no CWE" bucket. */
export const UNCLASSIFIED_VULN_CLASS = "unclassified";

/** The default ?value= for the vuln-class dimension (no filter; show every bucket). */
export const DEFAULT_VULN_CLASS_DIMENSION_VALUE = "all";

/**
 * Map a single raw CWE id ("CWE-79", case-insensitive) to its class slug.
 * Falls back to `other` for any unrecognised CWE.
 */
export function classForCwe(cweId: string): string {
  const normalised = cweId.toUpperCase().trim();
  for (const bucket of VULN_CLASS_BUCKETS) {
    if (bucket.cwes.includes(normalised)) return bucket.value;
  }
  return "other";
}

/**
 * Given the set of CWE ids attached to ONE CVE, pick the single dominant class
 * via {@link VULN_CLASS_PRIORITY}. Used by the backfill to populate
 * `cve_vuln_class` (one row per CVE). Returns `other` when the CVE's CWEs all
 * map to `other` (or — defensively — when the set is empty, though the backfill
 * only ever calls this for CVEs that have at least one CWE row).
 */
export function dominantVulnClass(cweIds: string[]): {
  vulnClass: string;
  cweCount: number;
} {
  const classes = new Set(cweIds.map(classForCwe));
  for (const slug of VULN_CLASS_PRIORITY) {
    if (classes.has(slug)) return { vulnClass: slug, cweCount: cweIds.length };
  }
  return { vulnClass: "other", cweCount: cweIds.length };
}

/** Display label for a vuln-class value. Returns the value itself as fallback. */
export function vulnClassLabel(value: string): string {
  return VULN_CLASS_BUCKETS.find((b) => b.value === value)?.label ?? value;
}
